import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Scale, DollarSign } from 'lucide-react';
import CTA from '../../components/CTA';

const NewryWeightManagement: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Weight Loss Hypnotherapy Newry | Affordable Mindful Eating Support</title>
        <meta
          name="description"
          content="Weight management hypnotherapy serving Newry from Belfast clinic (35 miles). Affordable, realistic approach to emotional eating and sustainable weight loss. CNHC registered."
        />
        <meta name="keywords" content="weight loss hypnotherapy Newry, affordable weight management Newry, mindful eating Newry, emotional eating Newry, hypnotherapy Newry weight" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/newry-weight-management/" />
        <meta property="og:title" content="Weight Loss Hypnotherapy Newry | Affordable Mindful Eating Support" />
        <meta property="og:description" content="Weight management hypnotherapy serving Newry. Affordable, realistic approach to emotional eating and sustainable weight loss." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Weight Management Newry",
            "description": "CNHC registered weight management hypnotherapy serving Newry from Belfast clinic. Affordable, realistic approach to emotional eating and sustainable lifestyle change.",
            "url": "https://www.sharonclare-hypnotherapy.com/newry-weight-management/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Weight Management Hypnotherapy",
            "areaServed": {
              "@type": "City",
              "name": "Newry",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Weight Loss Hypnotherapy Serving Newry
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Affordable, realistic support for sustainable weight loss and mindful eating
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">37 miles</span> from Newry via A1 • <span className="font-semibold">45-50 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Online sessions also available for Newry residents</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Weight Management Hypnotherapy for Newry
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                I serve the Newry community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, approximately 37 miles north via the A1 dual carriageway. The journey takes 45-50 minutes in typical traffic, providing accessible weight management support without the complexities of cross-border healthcare navigation. For Northern Ireland's border city residents, this offers straightforward access to professional hypnotherapy support.
              </p>
              <p>
                Let's be honest about weight loss. You've probably tried multiple diets, spent money on programs that promised quick results, and experienced the frustration of losing weight only to regain it. The weight loss industry profits from selling you the same solution repeatedly, blaming you when it doesn't work long-term. But the problem isn't you - it's the approach. Diets fail because they focus on restriction and willpower, ignoring the emotional and stress-related reasons you eat when you're not actually hungry.
              </p>
              <p>
                Hypnotherapy for weight management addresses the real issue: your relationship with food, stress eating patterns, emotional triggers, and the subconscious beliefs keeping you stuck. This isn't about celebrity diets or expensive meal replacement programs. It's about understanding why you eat, addressing the emotional patterns driving overeating, and creating sustainable changes that actually last. The approach is realistic, affordable, and focused on long-term results rather than dramatic short-term losses that inevitably rebound.
              </p>
              <p>
                For Newry clients, I also offer online sessions via video call - same professional treatment, no travel required. Many working-class families find online sessions more practical and affordable when factoring in travel costs and time away from work or family responsibilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Realistic Approach */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              A Realistic, Affordable Approach to Weight Loss
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <DollarSign className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  No Expensive Programs or Upselling
                </h3>
                <p className="text-gray-700">
                  You can book an initial consultation, individual follow-up sessions, or a discounted 4-session package. That's it. No meal replacements to buy, no expensive supplements, no upselling to additional programs. You know exactly what you're paying upfront. For Newry families managing tight budgets, this transparency matters. I also offer payment plans if the 4-session package is needed but the upfront cost is challenging.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Brain className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Addressing Real-Life Eating Patterns
                </h3>
                <p className="text-gray-700">
                  We work with your actual life, not an idealized version. If you're eating because you're stressed about money, tired from work, or using food as one of the few pleasures in a difficult week, we address those patterns with compassion and understanding. No judgment, no shame, just practical strategies for creating sustainable change within your real life circumstances.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Heart className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  No Quick Fix Promises
                </h3>
                <p className="text-gray-700">
                  I won't promise you'll lose 2 stone in 2 months. That's not realistic or sustainable. What I will promise is that we'll address the emotional and stress-related patterns driving your eating, teach you strategies that work in real life, and focus on gradual, sustainable weight loss that you can maintain. Slow and steady wins this race - and keeps the weight off long-term.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Scale className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Sustainable Lifestyle Change
                </h3>
                <p className="text-gray-700">
                  The goal isn't just weight loss - it's changing your relationship with food so weight loss becomes a natural consequence. When you're not eating emotionally, when stress doesn't automatically trigger eating, when you can recognize genuine hunger and fullness, weight management becomes far easier. These changes last because they're addressing root causes, not just symptoms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              How Hypnotherapy Works for Weight Management
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Hypnotherapy works at a subconscious level where your eating habits and emotional patterns are stored. When you eat emotionally - turning to food when you're stressed, bored, upset, or celebrating - that's your subconscious mind running automatic patterns learned over years or decades. Conscious willpower alone can't override these deeply ingrained patterns, which is why diets that rely purely on willpower inevitably fail.
              </p>
              <p>
                During hypnotherapy, you're in a deeply relaxed state where your subconscious mind is more receptive to positive suggestions and new patterns. We work on breaking the connection between difficult emotions and eating, building healthier stress management strategies, reconnecting you with genuine hunger and fullness signals, and changing the subconscious beliefs that keep you stuck (like "I'll always struggle with my weight" or "Food is my only comfort").
              </p>
              <p>
                The changes happen gradually but feel natural. You'll find yourself making different choices without the internal battle - stopping when you're satisfied rather than finishing everything on your plate, choosing healthier options because you genuinely prefer them, and noticing when you're eating emotionally and having the ability to choose differently. These aren't forced changes that require constant vigilance - they become your new normal.
              </p>
              <p>
                For Newry clients, the practical reality is 4-8 sessions over a few months (most people find 4-6 sufficient), plus daily audio recordings to reinforce the changes. It's a time and financial investment, but compare it to the thousands spent on diets, gym memberships, and weight loss programs over the years - programs that didn't create lasting change. This approach addresses the root cause, which is why the results last.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Newry Clients Say About Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "After spending hundreds on Weight Watchers and Slimming World over the years, I was skeptical. But Sharon's approach was completely different - addressing why I was eating, not just what. I've lost 2.5 stone over 7 months and it feels sustainable. The investment was worth it because it's actually working long-term."
                </p>
                <p className="font-semibold text-gray-900">C.M., Newry - Sustainable Weight Loss</p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The online sessions were perfect - no travel costs, no time away from the kids. Sharon understood the real-life pressures I was dealing with and didn't judge. I've lost over a stone and more importantly, I'm not stress eating every evening anymore. The payment plan made it affordable when I needed it."
                </p>
                <p className="font-semibold text-gray-900">L.K., Newry - Working Mother</p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I'd tried every diet going and nothing stuck. Sharon's approach addressed the emotional eating that was the real problem. No upselling, no expensive supplements, just honest support. I've lost 3 stone over a year and kept it off. That's never happened before."
                </p>
                <p className="font-semibold text-gray-900">T.B., Warrenpoint - Emotional Eating</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Newry Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How much does weight management hypnotherapy cost?
                </h3>
                <p className="text-gray-700">
                  Your initial consultation runs 90 minutes and includes your first hypnotherapy session. Follow-up sessions are 60 minutes, or you can purchase a discounted 4-session package. There are no hidden costs, no expensive supplements to buy, and no upselling to additional programs. For Newry clients traveling to Belfast, I also offer online sessions at the same rates, which saves travel costs. If the 4-session package cost is challenging, I can arrange payment plans to make it more manageable.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is this realistic or just another quick-fix program?
                </h3>
                <p className="text-gray-700">
                  This is the opposite of a quick-fix program. I won't promise you'll lose 2 stone in 2 months or fit into a specific dress size by a wedding. Those promises are unrealistic and set you up for disappointment and rebound weight gain. What I will promise is that we'll address the emotional and stress-related patterns driving your eating behaviors, and that if you engage with the process, you'll see gradual, sustainable weight loss over months, not weeks. The slower approach creates lasting change because we're fixing the root cause, not just forcing short-term restriction through willpower.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Will I have to buy expensive meal plans or supplements?
                </h3>
                <p className="text-gray-700">
                  No. I don't sell meal plans, meal replacements, supplements, or any additional products. The only cost is the hypnotherapy sessions themselves. I'm not a dietitian and don't provide specific dietary advice - my focus is entirely on the psychological and emotional aspects of eating. You'll eat normal food from your regular shopping. The changes come from addressing why you eat when you're not hungry, not from following a restrictive meal plan or buying special products.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can online sessions really work as well as face-to-face?
                </h3>
                <p className="text-gray-700">
                  Yes, online hypnotherapy sessions are just as effective as in-person sessions. The hypnotherapy process works equally well through video call, and for many Newry clients, online sessions are more practical - no travel time or costs, no time away from work or family, and easier to maintain consistency. You need a quiet, private space and a stable internet connection, but beyond that, the therapeutic process is identical. Many clients actually prefer online sessions because they feel more comfortable and relaxed in their own home environment.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions will I realistically need?
                </h3>
                <p className="text-gray-700">
                  Most clients find 4-8 sessions sufficient for weight management, with 4-6 being most common. This depends on how deeply entrenched your emotional eating patterns are, how much stress you're dealing with, and how much weight you want to lose. We'll assess progress as we go and adjust accordingly. Some clients achieve their goals in 4 sessions; others benefit from 6-8 sessions for more comprehensive change. I'll be honest with you about what I think you need - I won't recommend more sessions than necessary because I want you to trust the process and feel it's worth the investment.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is the journey from Newry worth it for weight loss hypnotherapy?
                </h3>
                <p className="text-gray-700">
                  That's a personal decision only you can make. What I can tell you is that many Newry clients feel the journey is worthwhile when compared to years of yo-yo dieting, spending money on programs that don't create lasting change, and the emotional toll of repeated failure. The journey is 45-50 minutes, and for a 4-6 session programme over a few months, that's manageable for most people. Alternatively, online sessions provide the same professional treatment without any travel. Consider what you've already spent on diets, gym memberships, and weight loss programs that didn't work long-term - this approach addresses the root cause, which is why the investment often pays off.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default NewryWeightManagement;
